/**
 * Avatar — round identity glyph with deterministic initials fallback.
 *
 * Renders one of three layers, in order of priority:
 *   1. The image at `src` when it loads successfully.
 *   2. The supplied `initials` (or initials derived from `name`).
 *   3. A neutral fallback glyph.
 *
 * The background color is derived deterministically from the `name`
 * (or `id`) hash so the same person always picks the same accent —
 * useful in commit-author rows, governance approver lists, and any
 * provenance view where the surface needs a stable identity color
 * without storing one per user.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useState } from "react";
import { cn } from "../styling/index.js";

export type AvatarSize = "xs" | "sm" | "md" | "lg";

export interface AvatarProps {
  /** Person / entity name — drives the initials fallback and accent hash. */
  readonly name: string;
  /** Optional explicit initials override. */
  readonly initials?: string;
  /** Optional image URL. */
  readonly src?: string;
  /** Optional stable identifier — used for hashing when `name` should not be. */
  readonly id?: string;
  /** Size preset. Defaults to `md`. */
  readonly size?: AvatarSize;
  /** Optional className appended to the wrapper. */
  readonly className?: string;
  /** Accessible label override. Defaults to `name`. */
  readonly ariaLabel?: string;
}

const SIZE_PX: Readonly<Record<AvatarSize, number>> = {
  xs: 20,
  sm: 28,
  md: 36,
  lg: 48,
};

const FONT_SIZE_PX: Readonly<Record<AvatarSize, number>> = {
  xs: 9,
  sm: 11,
  md: 14,
  lg: 18,
};

/** Deterministic hue (HSL) keyed by string hash. */
function hueFromString(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash % 360;
}

function deriveInitials(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return "?";
  const parts = trimmed.split(/\s+/).filter((part) => part.length > 0);
  if (parts.length === 0) return "?";
  const firstPart = parts[0] ?? "";
  if (parts.length === 1) {
    return firstPart.slice(0, 2).toUpperCase();
  }
  const lastPart = parts[parts.length - 1] ?? "";
  const first = firstPart.charAt(0);
  const last = lastPart.charAt(0);
  return `${first}${last}`.toUpperCase();
}

/** Renders a round identity glyph with image / initials / fallback layers. */
export const Avatar = React.memo(function Avatar({
  name,
  initials,
  src,
  id,
  size = "md",
  className,
  ariaLabel,
}: AvatarProps): React.JSX.Element {
  const [imageFailed, setImageFailed] = useState(false);
  const dimension = SIZE_PX[size];
  const fontSize = FONT_SIZE_PX[size];
  const hashSource = id ?? name;
  const hue = hueFromString(hashSource);
  const background = `hsl(${hue}, 60%, 88%)`;
  const foreground = `hsl(${hue}, 45%, 30%)`;
  const showImage = src !== undefined && !imageFailed;
  const resolvedInitials = initials ?? deriveInitials(name);

  return (
    <span
      role="img"
      aria-label={ariaLabel ?? name}
      data-csk-avatar-size={size}
      className={cn("inline-flex items-center justify-center overflow-hidden", className)}
      style={{
        background: showImage ? "transparent" : background,
        borderRadius: "9999px",
        color: foreground,
        fontFamily: "ui-sans-serif, system-ui, sans-serif",
        fontSize,
        fontWeight: 700,
        height: dimension,
        letterSpacing: "0.02em",
        verticalAlign: "middle",
        width: dimension,
      }}
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          width={dimension}
          height={dimension}
          onError={() => {
            setImageFailed(true);
          }}
          style={{ height: "100%", objectFit: "cover", width: "100%" }}
        />
      ) : (
        <span data-csk-avatar-initials>{resolvedInitials}</span>
      )}
    </span>
  );
});
